


















































































































import Vue from 'vue'
import Component from 'vue-class-component'
import { ApiKey, ApiKeyData, ApiKeyMode } from '@/core/Types'
import { FieldValue, QuerySnapshot, Timestamp } from '@/extern/FirebaseTypes'
import appManager from '@/services/AppManager'
import * as db from '@/core/Database'
import * as api from '@/services/ApiService'
import Spinner from '@/components/Spinner.vue'
import { AppColors } from '@/model/AppColors'
import { getDateText } from '@/model/Helpers'
import CreateApiKeyDialog from "@/components/sdk/CreateApiKeyDialog.vue";

@Component({
    components: {
        Spinner,
        CreateApiKeyDialog,
    }
})
export default class ApiKeysView extends Vue {
    companyId: string|null = null
    stripeCustomerId: string|undefined

    // api keys
    apiKeysSnapshotCounter: number = 0 // simple way to force a refresh when new items arise
    apiKeys: ApiKey[] = []
    apiKeysUnsubscriber: VoidFunction|null = null

    // visible api keys
    visibleApiKeys = new Set<string>()
    visibleApiKeysCounter: number = 0 // simple way to force refresh when

    // create api key dialog
    isCreateApiKeyDialogVisible: boolean = false
    createApiKeyDialogCounter: number = 0 // simple way to reset the dialog each time it is opened

    // changing api keys (activating, deactivating, deleting)
    changingApiKeysCounter: number = 0 // simple way to force refresh when
    activatingApiKey: ApiKey|null = null
    deactivatingApiKey: ApiKey|null = null
    isDeactivateApiKeyDialogVisible: boolean = false
    isDeactivatingApiKey: boolean = false

    // noinspection JSUnusedGlobalSymbols
    async created() {
        const user = appManager.userManager.state?.user
        if (user && user.data.companyId) {
            this.apiKeysUnsubscriber = db.streamApiKeys(
                { companyId: user.data.companyId },
                snapshot => this.onApiKeysReceived(snapshot),
                error => this.onApiKeysError(error)
            )

            this.companyId = user.data.companyId
        }
    }

    // noinspection JSUnusedGlobalSymbols
    beforeDestroy() {
        if (this.apiKeysUnsubscriber)
            this.apiKeysUnsubscriber()
    }

    onApiKeysReceived(snapshot: QuerySnapshot) {
        this.apiKeys = snapshot.docs
            .filter(doc => doc.exists)
            .map(doc => {
                return {
                    id: doc.id,
                    data: doc.data() as ApiKeyData
                }
            })

        // sort by date
        this.apiKeys.sort((u1, u2) => {
            const t1 = (u1.data.createdTimestamp as Timestamp)?.toMillis() || 0
            const t2 = (u2.data.createdTimestamp as Timestamp)?.toMillis() || 0
            return t1 - t2
        })

        this.apiKeysSnapshotCounter += 1
    }

    onApiKeysError(error: Error) {
        appManager.alertManager.publishError(`Error receiving API Keys: ${error}`)
    }

    getApiKeyIcon(apiKey: ApiKey): string {
        if (apiKey.data.mode === ApiKeyMode.PROD)
            return 'mdi-database-outline'

        return 'mdi-laptop'
    }

    getApiKeyIconColor(apiKey: ApiKey): string {
        if (apiKey.data.mode === ApiKeyMode.PROD)
            return AppColors.success

        return AppColors.logoBlue2
    }

    getApiKeyModeText(apiKey: ApiKey): string {
        if (apiKey.data.mode === ApiKeyMode.PROD)
            return 'PROD'

        return 'DEV'
    }

    getApiKeyName(apiKey: ApiKey): string {
        return apiKey?.data?.name || ''
    }

    getApiKeyDate(apiKey: ApiKey): string {
        return this.getDateTextLocal(apiKey?.data?.createdTimestamp)
    }

    getDateTextLocal(timestamp: Timestamp|FieldValue|undefined) {
        return timestamp ? getDateText(timestamp) : '---'
    }

    isApiKeyVisible(apiKey: ApiKey): boolean {
        return this.visibleApiKeys.has(apiKey.id)
    }

    onShowApiKeyPressed(apiKey: ApiKey) {
        this.visibleApiKeys.add(apiKey.id)
        this.visibleApiKeysCounter += 1
    }

    async onActivateApiKeyPressed(apiKey: ApiKey) {
        this.activatingApiKey = apiKey
        this.changingApiKeysCounter += 1

        await this.editApiKey(apiKey.id, true, 'Activate')

        this.activatingApiKey = null
        this.changingApiKeysCounter += 1
    }

    onDeactivateApiKeyPressed(apiKey: ApiKey) {
        this.deactivatingApiKey = apiKey
        this.changingApiKeysCounter += 1
        this.isDeactivatingApiKey = false
        this.isDeactivateApiKeyDialogVisible = true
    }

    onCancelDeactivateApiKey() {
        this.deactivatingApiKey = null
        this.changingApiKeysCounter += 1
        this.isDeactivatingApiKey = false
        this.isDeactivateApiKeyDialogVisible = false
    }

    async onDeactivateApiKeyConfirmed() {
        if (!this.deactivatingApiKey)
            return

        this.isDeactivatingApiKey = true

        await this.editApiKey(this.deactivatingApiKey.id, false, 'Deactivate')

        this.deactivatingApiKey = null
        this.isDeactivatingApiKey = false
        this.isDeactivateApiKeyDialogVisible = false
    }

    async editApiKey(apiKey: string, newIsActive: boolean, messageType: string): Promise<void> {
        try {
            await api.editApiKey({
                apiKey: apiKey,
                newIsActive: newIsActive,
            })

            appManager.alertManager.publishSuccess(`${messageType} API Key Successful`)
        } catch (error) {
            appManager.alertManager.publishError(`${messageType} API Key Error: ${error}`)
        }
    }

    isChangingApiKey(apiKey: ApiKey): boolean {
        return apiKey.id === this.activatingApiKey?.id || apiKey.id === this.deactivatingApiKey?.id
    }

    onAddApiKeyPressed() {
        this.createApiKeyDialogCounter += 1
        this.isCreateApiKeyDialogVisible = true
    }

    onCloseCreateApiKeyDialogRequested() {
        this.isCreateApiKeyDialogVisible = false
    }
}
